import type { BacktestPosition, DailyBar } from '@fleece/models';
import { max, min } from 'd3-array';
import { scaleBand, scaleLinear, type ScaleBand, type ScaleLinear } from 'd3-scale';

export interface ChartMargins {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

export const MARGINS: ChartMargins = { top: 8, right: 44, bottom: 20, left: 4 };

/** Vertical pitch between stacked position markers, and their gap above the day's high. */
export const DOT_PITCH = 7;
export const DOT_GAP = 6;

export interface PlacedDot {
  readonly position: BacktestPosition;
  /** Index into the chart's positions, stable across renders, for selection. */
  readonly index: number;
  readonly x: number;
  readonly y: number;
}

export interface PriceChartModel {
  readonly x: ScaleBand<string>;
  readonly y: ScaleLinear<number, number>;
  readonly innerWidth: number;
  readonly innerHeight: number;
  /** Body width of a candle: the band, but never under a pixel, so two years still draw as candles. */
  readonly candleWidth: number;
  /**
   * The latest close and where it sits, for the line drawn across the plot. Absent when it is
   * outside the visible price range — zoomed into a stretch it never came near.
   */
  readonly lastClose?: { readonly price: number; readonly y: number };
  readonly dots: ReadonlyArray<PlacedDot>;
  readonly yTicks: ReadonlyArray<number>;
  readonly xTicks: ReadonlyArray<string>;
}

/**
 * Gap between candles as a share of each day's slot. A quarter reads well at a few months;
 * at two years a slot is two or three pixels, and the same quarter would leave bodies
 * thinner than the gaps between them.
 */
function candlePadding(slotPx: number): number {
  return slotPx >= 6 ? 0.25 : 0.1;
}

/**
 * Scales, and where each backtest position sits: stacked upward from just above its
 * entry day's high, one pitch apart, so a day that opened six spreads is a column of six.
 *
 * The y domain reserves headroom for the tallest stack, measured in pixels and converted
 * back to price, so a column never runs off the top of the plot.
 */
export function buildPriceChartModel(
  bars: ReadonlyArray<DailyBar>,
  positions: ReadonlyArray<BacktestPosition>,
  width: number,
  height: number,
  /** The latest close, which a zoomed chart may not be showing. Defaults to the last bar given. */
  latestClose: number | undefined = bars.at(-1)?.close,
): PriceChartModel {
  const innerWidth = Math.max(0, width - MARGINS.left - MARGINS.right);
  const innerHeight = Math.max(0, height - MARGINS.top - MARGINS.bottom);
  const dates = bars.map((bar) => bar.date);
  const x = scaleBand<string>()
    .domain(dates)
    .range([0, innerWidth])
    .paddingInner(candlePadding(innerWidth / Math.max(1, dates.length)))
    .paddingOuter(0.1);

  const byDate = new Map(bars.map((bar) => [bar.date, bar]));
  const stacks = new Map<string, number>();
  for (const position of positions) {
    if (byDate.has(position.entryDate)) {
      stacks.set(position.entryDate, (stacks.get(position.entryDate) ?? 0) + 1);
    }
  }
  const tallest = max(stacks.values()) ?? 0;
  const headroomPx = tallest === 0 ? 0 : DOT_GAP + tallest * DOT_PITCH;

  const low = min(bars, (bar) => bar.low) ?? 0;
  const high = max(bars, (bar) => bar.high) ?? 1;
  const pricePerPx = innerHeight > headroomPx ? (high - low) / (innerHeight - headroomPx) : 0;
  const y = scaleLinear()
    .domain([low, high + headroomPx * pricePerPx])
    .range([innerHeight, 0])
    .nice();

  const placed: PlacedDot[] = [];
  const seen = new Map<string, number>();
  positions.forEach((position, index) => {
    const bar = byDate.get(position.entryDate);
    const left = x(position.entryDate);
    if (bar === undefined || left === undefined) {
      return;
    }
    const slot = seen.get(position.entryDate) ?? 0;
    seen.set(position.entryDate, slot + 1);
    placed.push({ position, index, x: left + x.bandwidth() / 2, y: y(bar.high) - DOT_GAP - slot * DOT_PITCH });
  });

  const tickEvery = Math.max(1, Math.ceil(dates.length / Math.max(1, Math.floor(innerWidth / 70))));
  return {
    x,
    y,
    innerWidth,
    innerHeight,
    candleWidth: Math.max(1, x.bandwidth()),
    lastClose: latestClose === undefined || bars.length === 0 || y(latestClose) < 0 || y(latestClose) > innerHeight ? undefined : { price: latestClose, y: y(latestClose) },
    dots: placed,
    yTicks: y.ticks(Math.max(2, Math.floor(innerHeight / 40))),
    xTicks: dates.filter((_, i) => i % tickEvery === 0),
  };
}

/** The bar under a pointer's x, or undefined off the plot. */
export function barAt(model: PriceChartModel, bars: ReadonlyArray<DailyBar>, pointerX: number): DailyBar | undefined {
  if (bars.length === 0 || pointerX < 0 || pointerX > model.innerWidth) {
    return undefined;
  }
  const step = model.x.step();
  const first = model.x(bars[0].date) ?? 0;
  const index = Math.floor((pointerX - first + (step * model.x.paddingInner()) / 2) / step);
  return bars[Math.min(bars.length - 1, Math.max(0, index))];
}
